import { NEARBY_SUPS_QUERY } from "@/gql/nearby-sups";
import { type NearbyProperty, type SellerUnitAndProject } from "@/types/sup";
import { getApolloData } from "@/utils/apollo/get-data";

// Transform the basic schema response to the expected format
export function transformNearbyProperty(property: NearbyProperty, type: "unit" | "sp"): SellerUnitAndProject {
	return {
		...property,
		_id: property._id,
		// Add default values for missing fields that components expect
		facings: [],
		parkings: property.parkings || [],
		propertyTypes: [],
		possessionStatuses: [],
		currentRate: 0,
		mediaList: [],
		type: type,
	};
}

interface GetNearbySUPsParams {
	coordinates: { lat: number; lng: number };
	type: "unit" | "sp";
	unitId?: string; // Optional: for ranked/filtered results
	limit?: number;
	searchAfter?: string;
	searchBefore?: string;
	excludeId?: string;
}

interface GetNearbySUPsReturn {
	data: NearbyProperty[] | null;
	error: string | null;
	hasMore: boolean;
	searchAfter: string | null;
	searchBefore: string | null;
	total: number;
}

export async function getNearbySUPs({
	coordinates,
	type,
	unitId,
	limit = 20,
	searchAfter,
	searchBefore,
	excludeId,
}: GetNearbySUPsParams): Promise<GetNearbySUPsReturn> {
	try {
		const { data, error } = await getApolloData<{
			sellerUnitsAndProjects: {
				getNearby: {
					data: NearbyProperty[];
					hasMore: boolean;
					searchAfter: string;
					searchBefore: string;
					total: number;
				};
			};
		}>({
			query: NEARBY_SUPS_QUERY,
			variables: {
				latitude: coordinates.lat.toString(),
				longitude: coordinates.lng.toString(),
				type,
				unitId, // Optional - enables ranked filtering when provided
				limit,
				searchAfter,
				searchBefore,
				excludeId,
			},
			fetchPolicy: "cache-first",
		});

		if (error) {
			return {
				data: null,
				error: error instanceof Error ? error.message : "GraphQL error occurred",
				hasMore: false,
				searchAfter: null,
				searchBefore: null,
				total: 0,
			};
		}

		const result = data?.sellerUnitsAndProjects?.getNearby;
		if (!result) {
			return {
				data: null,
				error: "No data received",
				hasMore: false,
				searchAfter: null,
				searchBefore: null,
				total: 0,
			};
		}

		return {
			data: result.data || [],
			error: null,
			hasMore: result.hasMore || false,
			searchAfter: result.searchAfter,
			searchBefore: result.searchBefore,
			total: result.total || 0,
		};
	} catch (err) {
		return {
			data: null,
			error: err instanceof Error ? err.message : "Failed to fetch nearby SUPs",
			hasMore: false,
			searchAfter: null,
			searchBefore: null,
			total: 0,
		};
	}
}
